import React, { useState, useEffect, useRef } from 'react';
import {
  TextInput, Button, Paragraph, Title, ActivityIndicator, Checkbox, Dialog, HelperText, Portal,
} from 'react-native-paper';
import {
  Platform, View, Keyboard, Alert, StyleSheet, KeyboardAvoidingView,
} from 'react-native';
import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import CodeInput from 'react-native-confirmation-code-field';
import validator from 'validator';
import color from 'color';

import TouchableExternalLink from '../components/TouchableExternalLink';
import { useColors } from '../contexts/theme';
import l from '../utils/localization';
import { useSetToken } from '../modules/auth/actions-common';
import { logEvent } from '../utils/analytics';
import bugsnagClient from '../clients/bugsnag-client';


const styles = StyleSheet.create({
  marginTopBottom: { marginVertical: 10 },
  helperText: { marginTop: 60 },
  codeContainer: {
    alignItems: 'center', justifyContent: 'flex-start',
  },
  checkboxWrapper: { flexDirection: 'row', alignItems: 'center' },
  wrapper: {
    padding: 20, maxWidth: 500, flex: 1, alignSelf: 'center', minWidth: 300,
  },
  instructionText: { paddingVertical: 20 },
  bold: { fontWeight: 'bold' },
  flexOne: {
    flex: 1,
  },
  emailWrapper: {
    flexDirection: 'row', flex: 1,
  },
});

const hitSlop = {
  left: 20, right: 20, top: 20, bottom: 20,
};

const LoginRequestMutation = gql`mutation authRequest($email:String!){
  authRequest(email:$email)
}
`;

const LoginRequestWithConsentMutation = gql`mutation authRequestWithTerms($email:String!, $acceptedTermsAt:DateTime!, $acceptedPrivacyPolicyAt:DateTime!){
  authRequestWithTerms(email:$email, acceptedPrivacyPolicyAt: $acceptedPrivacyPolicyAt, acceptedTermsAt: $acceptedTermsAt)
}
`;

const ConfirmLogin = gql`mutation authConfirm($code:String!, $email:String!) {
  authConfirm(email:$email, code:$code)
}
`;

const ConsentDialog = ({ onSubmit, visible, onDismiss }) => {
  const [acceptedTermsAt, setAcceptedTermsAt] = useState(null),
        colors = useColors(),
        [acceptedPrivacyPolicyAt, setAcceptedPrivacyPolicyAt] = useState(null);

  const onSubmitPress = () => {
    if (acceptedTermsAt && acceptedPrivacyPolicyAt) {
      onSubmit({ acceptedTermsAt, acceptedPrivacyPolicyAt });
    } else {
      Alert.alert(l.terms, l.pleaseReviewAndAcceptToProceed);
    }
  };

  useEffect(() => {
    if (visible) {
      Keyboard.dismiss();
    }
  }, [visible]);

  return (
    <Dialog onDismiss={onDismiss} visible={visible}>
      <Dialog.Title>{ l.weValueYourPrivacy }</Dialog.Title>
      <Dialog.Content>
        <Paragraph style={styles.instructionText}>
          {l.pleaseTakeAMinuteToReviewAndAccept}
        </Paragraph>

        <View style={styles.checkboxWrapper}>
          <Checkbox.Android
            status={acceptedPrivacyPolicyAt ? 'checked' : 'unchecked'}
            uncheckedColor={colors.secondary}
            hitSlop={hitSlop}
            color={colors.secondary}
            onPress={() => {
              setAcceptedPrivacyPolicyAt((previous) => previous
                ? null
                : new Date());
            }}
          />
          <TouchableExternalLink url='https://docs.google.com/document/d/e/2PACX-1vSi2nLO_iYWxUZ86cNgHh3i6fvR9fEG098IL4Ml-QsOcspCCf9YN-5_V0aJOXGiQU8bgu0oW-26qRBa/pub'>
            <Paragraph style={styles.instructionText}>
              {l.pleaseReviewAndAcceptOur}
              <Paragraph style={styles.bold}>{` ${l.privacyPolicy}`}</Paragraph>
            </Paragraph>
          </TouchableExternalLink>

        </View>

        <View style={styles.checkboxWrapper}>
          <Checkbox.Android
            status={acceptedTermsAt ? 'checked' : 'unchecked'}
            uncheckedColor={colors.secondary}
            color={colors.secondary}
            onPress={() => { setAcceptedTermsAt((previous) => previous ? null : new Date()); }}
          />
          <TouchableExternalLink url='https://app.termly.io/document/terms-and-conditions/bc13edca-c490-46dc-8a7e-e62e25bc4835'>
            <Paragraph style={styles.instructionText}>
              {l.pleaseReviewAndAcceptOur}
              <Paragraph style={styles.bold}>{` ${l.termsOfService}`}</Paragraph>
            </Paragraph>
          </TouchableExternalLink>
        </View>
      </Dialog.Content>
      <Dialog.Actions>
        <Button color={colors.text} onPress={onDismiss}>{l.cancel}</Button>
        <Button color={colors.text} onPress={onSubmitPress}>
          {l.proceed}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

const EmailComponent = ({ onSubmit }) => {
  const [email, setEmail] = useState(''),
        [emailValidationMessage, setEmailValidationMessage] = useState(null),
        [showConsent, setShowConsent] = useState(false),
        colors = useColors(),
        [requestLogin, { data, error, loading }] = useMutation(LoginRequestMutation),
        [consentLogin, { data: consentData, error: consentError, loading: consentLoading }] = useMutation(LoginRequestWithConsentMutation),
        hasSentCode = (data && data.authRequest === 'accepted') || (consentData && consentData.authRequestWithTerms === 'accepted');


  const sendCodeRequest = async () => {
    if (validator.isEmail(email)) {
      logEvent('requested_code');
      setEmailValidationMessage(null);
      await requestLogin({ variables: { email } });
    } else {
      bugsnagClient.leaveBreadcrumb('Entered invalid email');
      setEmailValidationMessage(l.pleaseEnterValidEmail);
    }
  };

  useEffect(() => {
    if (hasSentCode) {
      onSubmit(email);
    }
  }, [onSubmit, hasSentCode, email]);

  const sendCodeRequestWithConsent = async ({ acceptedTermsAt, acceptedPrivacyPolicyAt }) => {
    if (validator.isEmail(email)) {
      logEvent('requested_code_with_consent');
      await consentLogin({ variables: { email, acceptedTermsAt, acceptedPrivacyPolicyAt } });
    } else {
      bugsnagClient.leaveBreadcrumb('Entered invalid email', email);
      Alert.alert(l.email, l.pleaseEnterValidEmail);
    }
  };

  useEffect(() => {
    if (data && data.authRequest !== 'accepted' && !loading) {
      logEvent('requesting_user_consent');
      setShowConsent(true);
    }
  }, [data, loading]);

  useEffect(() => {
    if (consentError) {
      bugsnagClient.leaveBreadcrumb('ConsentError', consentError);
      if (consentError.message) {
        Alert.alert('Error', consentError.message);
      } else {
        Alert.alert('Error', consentError);
      }
    }
  }, [consentError]);

  const platformSpecificTextInputProps = Platform.OS === 'web' ? {} : {
    importantForAutofill: 'yes',
    autoCompleteType: 'email',
  };

  return !hasSentCode ? (
    <>
      <View style={styles.emailWrapper}>
        <View style={styles.flexOne}>
          <Paragraph style={styles.instructionText}>
            {
              l.simplyEnterYourEmailAndWellSendYouALink
            }
          </Paragraph>
          <TextInput
            label='Email'
            value={email}
            autoCorrect={false}
            autoFocus
            style={{ backgroundColor: colors.surface }}
            blurOnSubmit
            onChangeText={setEmail}
            textContentType='emailAddress'
            keyboardType='email-address'
            autoCapitalize='none'
            onSubmitEditing={sendCodeRequest}
            {...platformSpecificTextInputProps}
          />
          <HelperText
            type='error'
            visible={emailValidationMessage || (error && !loading)}
          >
            { emailValidationMessage || (error && error.message)}
          </HelperText>

          <Button
            onPress={sendCodeRequest}
            loading={loading || consentLoading}
            style={{ marginVertical: 20 }}
            hitSlop={hitSlop}
            mode='contained'
          >
            {l.proceed}
          </Button>
        </View>
      </View>
      <Portal>
        <ConsentDialog
          onSubmit={sendCodeRequestWithConsent}
          onDismiss={() => setShowConsent(false)}
          visible={showConsent}
        />
      </Portal>
    </>
  ) : null;
};

const LoginRequest = () => {
  const setToken = useSetToken(),
        [email, setEmail] = useState(null),
        txtCode = useRef(),
        colors = useColors(),
        [requestLogin, { error, loading }] = useMutation(LoginRequestMutation),
        [confirmLogin, {
          data: authConfirmData,
          error: confirmError,
          loading: confirmLoading,
        }] = useMutation(ConfirmLogin);

  useEffect(() => {
    if (authConfirmData) {
      setToken(authConfirmData.authConfirm);
    }
  }, [authConfirmData, setToken]);


  const confirmCode = async (code) => {
    if (!validator.isEmail(email)) {
      Alert.alert('Email', l.pleaseEnterValidEmail);
      bugsnagClient.leaveBreadcrumb('Entered invalid email', email);
    } else if (code.length !== 6) {
      Alert.alert('Code', l.pleaseEnter6DigitCodeFromEmail);
      bugsnagClient.leaveBreadcrumb('Entered invalid code', code);
    } else {
      logEvent('login_succeeding');
      await confirmLogin({ variables: { email, code } });
    }
  };

  useEffect(() => {
    if (error) {
      if (error.message) {
        Alert.alert('Error', error.message);
      } else {
        Alert.alert('Error', error);
      }

      bugsnagClient.leaveBreadcrumb('Error', error);

      txtCode.current.focus();
    }
  }, [error]);

  useEffect(() => {
    if (confirmError) {
      txtCode.current.clear();
      txtCode.current.focus();
    }
    bugsnagClient.leaveBreadcrumb('Error when submitting code', error);
  }, [confirmError, error]);

  const resendCode = () => requestLogin({ variables: { email } });

  return (
    <View style={{ alignItems: Platform.OS === 'web' ? 'center' : undefined, flex: 1, backgroundColor: colors.background }}>
      <View style={styles.wrapper}>
        { Platform.OS === 'web' ? <Title>{l.login}</Title> : null }
        <EmailComponent onSubmit={(eml) => {
          setEmail(eml);
        }}
        />

        { email
          ? (
            <KeyboardAvoidingView>
              <View style={styles.codeContainer}>
                <Paragraph style={styles.instructionText}>
                  {
                    l.pleaseEnter6DigitCodeFromEmail
                  }
                </Paragraph>
                <CodeInput
                  onFulfill={confirmCode}
                  autoFocus
                  codeLength={6}
                  ref={txtCode}
                  keyboardType='number-pad'
                  activeColor={colors.text}
                  inactiveColor={color(colors.text).alpha(0.2).string()}
                />
                <HelperText
                  type='error'
                  style={styles.helperText}
                  visible={(confirmError && !confirmLoading)}
                >
                  { (confirmError && confirmError.message)}
                </HelperText>
                { !confirmLoading ? (
                  <>
                    <Paragraph style={styles.marginTopBottom}>{ l.didntGetTheCodeQuestion }</Paragraph>
                    <Button
                      style={styles.marginTopBottom}
                      loading={loading}
                      mode='outlined'
                      onPress={resendCode}
                    >
                      { l.resendCode }
                    </Button>
                  </>
                ) : (
                  <ActivityIndicator
                    animating={confirmLoading}
                    hidesWhenStopped
                  />
                ) }
              </View>
            </KeyboardAvoidingView>
          ) : null}
      </View>
    </View>
  );
};

export default LoginRequest;
